import { DEFAULT_GROUP_TIMEZONE, addDays, addMonths, formatYmd, localDateParts } from "./collection-schedule.js";

/**
 * 🔴 REPORT-EXPORT-VN-DAY-BOUNDARY (QĐ PO 01/10/2026, phương án B · @UC0048 @UC9514 @UC0064) — MỘT nguồn cho câu hỏi
 * *"ngày lịch `YYYY-MM-DD` ⇒ khoảng thời điểm nào"* của báo cáo/xuất sổ/lọc danh sách giao dịch: ngày là ngày lịch
 * **Asia/Ho_Chi_Minh** (`DEFAULT_GROUP_TIMEZONE` — repo không có múi giờ theo nhóm), không phải ngày UTC.
 *
 * Vì sao: `new Date("2026-10-01T00:00:00.000Z")` là nửa đêm UTC = **07:00 sáng giờ VN** ⇒ giao dịch 00:00–07:00 sáng ngày 01
 * rơi vào báo cáo tháng TRƯỚC, và 00:00–07:00 ngày 01 tháng sau lọt vào tháng NÀY (đo B0: hồ sơ `_runs/REPORT-EXPORT-VN-DAY-BOUNDARY`).
 *
 * Cách tính: Intl (múi giờ IANA, không hằng `+07:00` rải rác) + **tự kiểm vòng tròn** bằng `localDateParts` — mốc tìm được phải
 * rơi đúng vào ngày đã đặt tên, đọc theo CHÍNH múi giờ đó; không thì `null`. Ngày không có thật ("2026-02-30") cũng `null`:
 * `Date.parse` của V8 nhận "2026-02-30" và trôi IM LẶNG sang 02/03 — phép vòng tròn là chốt duy nhất bắt được.
 * Hàm THUẦN (không đồng hồ). Tiền: 0 (chỉ là biên lọc).
 */
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/u;
const DAY_MS = 24 * 60 * 60 * 1000;

function partsOf(ymd: string): { y: number; m: number; d: number } | null {
  const m = YMD_RE.exec(ymd);
  if (m === null) return null;
  const p = { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
  const probe = new Date(Date.UTC(p.y, p.m - 1, p.d));
  return probe.getUTCFullYear() === p.y && probe.getUTCMonth() === p.m - 1 && probe.getUTCDate() === p.d ? p : null;
}

/**
 * `YYYY-MM-DD` là một ngày lịch CÓ THẬT (đúng hình + tồn tại: 2028-02-29 có, 2026-02-29 không) **và dựng được CẢ HAI biên**
 * (`vnDayStartUtc` lẫn `vnDayEndExclusiveUtc` khác `null`).
 *
 * 🔴 Review #1120 MED-1: bản cũ chỉ hỏi "ngày có thật" ⇒ `9999-12-31` qua biên REST, rồi mốc hết ngày cần 00:00 ngày `10000-01-01` (không phải
 * `YYYY-MM-DD`) ⇒ `null` ⇒ admin ném `RangeError` ⇒ **500**. Vị-từ của biên phải là đúng câu caller sẽ hỏi sau đó — không phải một câu gần giống
 * (không thêm hằng "năm tối đa": trần thật là chỗ phép dựng biên thất bại, để nó tự nói).
 */
export function isVnCalendarYmd(ymd: string): boolean {
  return vnDayStartUtc(ymd) !== null && vnDayEndExclusiveUtc(ymd) !== null;
}

/**
 * Thời điểm UTC của 00:00 ngày `ymd` theo lịch VN (`timeZone` chỉ để test/đột biến — mặc định Asia/Ho_Chi_Minh).
 * `null` = không phải ngày có thật / múi giờ trôi (vòng tròn không khớp) — caller fail-loud, không đoán.
 */
export function vnDayStartUtc(ymd: string, timeZone: string = DEFAULT_GROUP_TIMEZONE): Date | null {
  if (partsOf(ymd) === null) return null;
  const utcMidnight = Date.parse(`${ymd}T00:00:00.000Z`);
  const offset = zoneOffsetMs(new Date(utcMidnight), timeZone);
  if (Number.isNaN(offset)) return null;
  const start = new Date(utcMidnight - offset);
  return vnYmdOf(start, timeZone) === ymd ? start : null;
}

/**
 * Độ lệch (ms) của giờ treo tường `timeZone` so với UTC tại thời điểm `at` (VN: +7h). Mili-giây của `at` không tham gia (Intl chỉ trả tới giây).
 * `NaN` khi Intl không trả đủ phần — caller fail-loud. Private: nguồn DUY NHẤT của phép "instant ⇒ giờ treo tường" trong file này.
 */
function zoneOffsetMs(at: Date, timeZone: string): number {
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(at);
  const part = (t: string): number => Number.parseInt(f.find((p) => p.type === t)?.value ?? "NaN", 10);
  const wall = Date.UTC(part("year"), part("month") - 1, part("day"), part("hour") % 24, part("minute"), part("second"));
  return wall - (at.getTime() - at.getUTCMilliseconds());
}

/** Mốc HẾT ngày `ymd` (exclusive) = 00:00 VN của ngày KẾ — "đến ngày X" tính trọn ngày X. `null` như `vnDayStartUtc`. */
export function vnDayEndExclusiveUtc(ymd: string, timeZone: string = DEFAULT_GROUP_TIMEZONE): Date | null {
  const p = partsOf(ymd);
  return p === null ? null : vnDayStartUtc(formatYmd(addDays(p, 1)), timeZone);
}

/** Ngày lịch VN (`YYYY-MM-DD`) chứa thời điểm `at`. */
export function vnYmdOf(at: Date, timeZone: string = DEFAULT_GROUP_TIMEZONE): string {
  return formatYmd(localDateParts(at, timeZone));
}

/**
 * 🔴 V-1 tiền (QĐ người dùng 18/10/2026 · @UC9566 @UC0038 @UC9358) — NGÀY LỊCH VN chứa thời điểm `at`, ở dạng giá trị của cột `@db.Date`
 * (nửa đêm UTC của chính ngày dương lịch đó — cùng dạng `periodStart`/`periodEnd`/`dueDate`/`effectiveFrom`/`effectiveTo` đã lưu).
 *
 * Dùng cho: "hôm nay" của luật hồi sinh kỳ thu (`collection-revival` · `reviveCurrentPeriodInTx` · kỳ hiện tại khi bật lại quỹ) và ngày
 * vào/rời nhóm của pro-rata (`membership-period.helpers`). Bản cũ cắt `at` theo ngày UTC ⇒ 00:00–07:00 sáng giờ VN còn là HÔM QUA: mở lại
 * được kỳ đã khép, hồi sinh nhầm kỳ trước, người vào nhóm bị đếm thêm một ngày hiện diện. Chỉ đổi cách GHI/SO bản ghi mới — không backfill.
 * Hàm THUẦN; một nguồn với `vnYmdOf` (cùng `localDateParts`).
 */
export function vnDateColumnOf(at: Date, timeZone: string = DEFAULT_GROUP_TIMEZONE): Date {
  const { y, m, d } = localDateParts(at, timeZone);
  return new Date(Date.UTC(y, m - 1, d));
}

/**
 * 🔴 V-1 (nghiệm thu máy ảo 18/10/2026 · @UC0059 @UC9623) — MỘT nguồn cho *"còn mấy ngày tới hạn"*: số ngày LỊCH VN từ hôm nay (ngày VN
 * chứa `now`) tới ngày hạn. 0 = hạn hôm nay · âm = đã qua hạn bấy nhiêu ngày.
 *
 * `dueDate` là cột `@db.Date` (nửa đêm UTC của chính ngày dương lịch đó) ⇒ đọc phần Y-M-D UTC của nó, KHÔNG dịch múi. Công thức cũ
 * `Math.ceil((dueDate − now)/ngày)` so ngày hạn với THỜI ĐIỂM now: 00:00 UTC = 07:00 VN ⇒ khung 00:00–07:00 VN dư 1 ngày ("còn 1 ngày" khi
 * hạn là hôm nay). Ban ngày 07:00–24:00 VN hai cách cho cùng số (khoá bằng test so hai công thức).
 * Hàm THUẦN; hai đầu đều là nửa đêm UTC nên hiệu chia hết cho một ngày (không DST trong UTC).
 */
export function vnDaysUntil(dueDate: Date, now: Date, timeZone: string = DEFAULT_GROUP_TIMEZONE): number {
  const today = localDateParts(now, timeZone);
  const dueDay = Date.UTC(dueDate.getUTCFullYear(), dueDate.getUTCMonth(), dueDate.getUTCDate());
  return Math.round((dueDay - Date.UTC(today.y, today.m - 1, today.d)) / DAY_MS);
}

/**
 * Chiều ĐỌC của mốc hết-ngày đã lưu (exclusive) ⇒ ngày cuối INCLUSIVE người dùng đã nhập: ngày VN của mốc, lùi MỘT ngày lịch.
 * Không dùng "mốc − 1 ms": job CŨ lưu biên UTC (`2026-11-01T00:00Z` = 07:00 VN 01/11) thì −1 ms vẫn là 01/11 ở VN; lùi một ngày lịch
 * thì cả job cũ lẫn job mới (`2026-10-31T17:00Z`) đều đọc ra 31/10.
 */
export function vnInclusiveEndYmd(exclusive: Date, timeZone: string = DEFAULT_GROUP_TIMEZONE): string {
  return formatYmd(addDays(localDateParts(exclusive, timeZone), -1));
}

/**
 * 🔴 REPORT-EXPORT-VN-DAY-BOUNDARY **B2** (QĐ PO 01/10/2026 phương án B · @UC0046 @UC0074 @UC0205 @UC0156) — THÁNG/TUẦN của số liệu tiền
 * người dùng đọc (thẻ "tháng này" + biểu đồ 6 tháng của màn Quỹ · trợ lý `READ-FUND-MONEY` · "tuần này" của HOME) dựng từ CHÍNH hai biên
 * ngày ở trên — không phép tính tháng/tuần thứ hai. Trước B2 ba chỗ đó tự dựng tháng/tuần UTC ⇒ 00:00–07:00 sáng giờ VN ngày 01 (hoặc sáng
 * Thứ Hai) rơi vào kỳ TRƯỚC, và trợ lý (tổng theo tháng UTC) lệch danh sách giao dịch cùng câu (ngày VN từ B1).
 */
export interface VnCalendarMonth {
  /** `YYYY-MM` — khoá cột biểu đồ, nhãn kỳ. */
  readonly key: string;
  /** Ngày đầu / ngày cuối (inclusive) `YYYY-MM-DD` — đúng dạng `from`/`to` của cửa lọc giao dịch. */
  readonly firstYmd: string;
  readonly lastYmd: string;
  /** [start, end): 00:00 VN ngày 01 → 00:00 VN ngày 01 tháng sau. `end` của tháng i === `start` của tháng i+1 (không hở, không chồng). */
  readonly start: Date;
  readonly end: Date;
}

/**
 * Tháng lịch VN chứa thời điểm `at`, dịch `offsetMonths` tháng (biểu đồ: −5..0). `null` khi biên không dựng được (tháng 12/9999) — caller
 * fail-loud, không đoán.
 */
export function vnMonthOf(at: Date, offsetMonths = 0, timeZone: string = DEFAULT_GROUP_TIMEZONE): VnCalendarMonth | null {
  const here = localDateParts(at, timeZone);
  const first = addMonths({ y: here.y, m: here.m, d: 1 }, offsetMonths);
  const lastDay = new Date(Date.UTC(first.y, first.m, 0)).getUTCDate();
  const firstYmd = formatYmd(first);
  const lastYmd = formatYmd({ ...first, d: lastDay });
  const start = vnDayStartUtc(firstYmd, timeZone);
  const end = vnDayEndExclusiveUtc(lastYmd, timeZone);
  if (start === null || end === null) return null;
  return { key: firstYmd.slice(0, 7), firstYmd, lastYmd, start, end };
}

/**
 * Tuần lịch VN chứa `at`: [00:00 VN Thứ Hai, 00:00 VN Thứ Hai tuần sau). Quy ước tuần (ISO, Thứ Hai đầu tuần, Chủ Nhật thuộc tuần bắt đầu
 * Thứ Hai TRƯỚC nó) giữ nguyên như `weekRange` cũ của HOME — chỉ đổi múi giờ. `null` khi biên không dựng được.
 */
export function vnIsoWeekOf(at: Date, timeZone: string = DEFAULT_GROUP_TIMEZONE): { readonly start: Date; readonly end: Date } | null {
  const here = localDateParts(at, timeZone);
  const isoDow = new Date(Date.UTC(here.y, here.m - 1, here.d)).getUTCDay() || 7; // 1 = Thứ Hai … 7 = Chủ Nhật
  const monday = addDays(here, 1 - isoDow);
  const start = vnDayStartUtc(formatYmd(monday), timeZone);
  const end = vnDayStartUtc(formatYmd(addDays(monday, 7)), timeZone);
  return start === null || end === null ? null : { start, end };
}

/**
 * ISO 8601 của `at` theo giờ treo tường VN KÈM offset (`2026-10-01T00:30:00.000+07:00`) — CÙNG instant với `toISOString()` (parse lại ra đúng
 * mili-giây), chỉ khác cách in: người mở tệp "tháng 10" thấy ngày 01/10 chứ không thấy `2026-09-30T17:30Z` (review #1120 MED-2, cột
 * `created_at` của CSV xuất sổ). Tự kiểm vòng tròn: chuỗi in ra phải parse về đúng `at`, không thì ném (lỗi lập trình, không phải dữ liệu).
 */
export function vnIsoString(at: Date, timeZone: string = DEFAULT_GROUP_TIMEZONE): string {
  const offset = zoneOffsetMs(at, timeZone);
  if (Number.isNaN(offset)) throw new Error(`vnIsoString: Intl không trả giờ treo tường của ${timeZone}.`);
  const sign = offset < 0 ? "-" : "+";
  const minutes = Math.abs(offset) / 60_000;
  const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
  const mm = String(minutes % 60).padStart(2, "0");
  const out = `${new Date(at.getTime() + offset).toISOString().slice(0, -1)}${sign}${hh}:${mm}`;
  if (Date.parse(out) !== at.getTime()) {
    throw new Error(`vnIsoString: không in được ${at.toISOString()} theo ${timeZone} (vòng tròn không khớp).`);
  }
  return out;
}
